import axios from "axios";
import { refreshToken as refreshJwtToken } from "./authService";

const API_BASE_URL = "/api";

// Add a simple request interceptor to include the token in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let isRefreshingToken = false;
let refreshQueue = [];

const resolveRefreshQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  refreshQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";
    const isAuthLoginRequest =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/register") ||
      requestUrl.includes("/auth/aad-login") ||
      requestUrl.includes("/auth/azure-login");

    if (!originalRequest || status !== 401) {
      return Promise.reject(error);
    }

    // Do not attempt token refresh for login/auth entry endpoints.
    // A 401 here means bad credentials or auth mismatch, not an expired access token.
    if (isAuthLoginRequest) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/auth/refresh")) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (isRefreshingToken) {
      try {
        const token = await new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        });
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    isRefreshingToken = true;

    try {
      const hasRefreshToken = Boolean(localStorage.getItem("refreshToken"));
      if (!hasRefreshToken) {
        resolveRefreshQueue(error, null);
        return Promise.reject(error);
      }

      const refreshResult = await refreshJwtToken();
      const newAccessToken = refreshResult?.access_token;
      if (!newAccessToken) {
        resolveRefreshQueue(error, null);
        return Promise.reject(error);
      }
      resolveRefreshQueue(null, newAccessToken || null);
      if (newAccessToken) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }
      return axios(originalRequest);
    } catch (refreshError) {
      resolveRefreshQueue(refreshError, null);
      return Promise.reject(refreshError);
    } finally {
      isRefreshingToken = false;
    }
  },
);

/** Calculates job costs */
const calculateCosts = async (jobData) => {
  try {
    // Ensure line_items have numbers where expected before sending
    const cleanData = {
      ...jobData,
      line_items: jobData.line_items.map((item) => {
        if (item.mode === "custom") {
          // Custom Mode: pass through as-is with quantity cleaned
          return {
            ...item,
            quantity: Number(item.quantity) || 1,
          };
        }
        // Typed line (Product Mode, product_type_key set): prices through the
        // rate-card engine by product type. Send product_type_key and OMIT
        // product_id — the two are mutually exclusive on the backend.
        if (item.product_type_key) {
          const { product_id, ...rest } = item;
          return {
            ...rest,
            product_type_key: item.product_type_key,
            material_id: Number(item.material_id) || null,
            length: Number(item.length) || 0,
            width: Number(item.width) || 0,
            height: Number(item.height) || 0,
            additional_units: Number(item.additional_units) || 0,
            additional_cost_per_unit:
              Number(item.additional_cost_per_unit) || 0,
            // Typed-line finish + waste (rate-card engine).
            finish_mode: item.finish_mode || null,
            waste_mode: item.waste_mode || null,
            waste_custom_pct:
              item.waste_custom_pct === null ||
              item.waste_custom_pct === undefined ||
              item.waste_custom_pct === ""
                ? null
                : Number(item.waste_custom_pct),
          };
        }
        // Product Mode (catalog product): clean numeric fields
        return {
          ...item,
          product_id: Number(item.product_id) || null,
          material_id: Number(item.material_id) || null,
          length: Number(item.length) || 0,
          width: Number(item.width) || 0,
          height: Number(item.height) || 0,
          additional_units: Number(item.additional_units) || 0,
          additional_cost_per_unit: Number(item.additional_cost_per_unit) || 0,
        };
      }),
    };
    const response = await axios.post(`${API_BASE_URL}/calculate`, cleanData);
    return response.data;
  } catch (error) {
    console.error(
      "API Calculation Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Calculation request failed");
  }
};

/** Fetches available products */
const getProducts = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/products`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Products Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch products");
  }
};

/** Fetches available product types (rate-card registry rows) */
const getProductTypes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/product-types`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Product Types Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch product types");
  }
};

/** Fetches available materials */
const getMaterials = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/materials`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch materials");
  }
};

/** Creates a new material */
const createMaterial = async (materialData) => {
  try {
    const token = localStorage.getItem("accessToken");
    // Ensure all numeric fields are sent as numbers
    const dataToSend = {
      ...materialData,
      cost_per_sqft: Number(materialData.cost_per_sqft) || 0,
      sheet_length_in:
        materialData.sheet_length_in !== null
          ? Number(materialData.sheet_length_in)
          : null,
      sheet_width_in:
        materialData.sheet_width_in !== null
          ? Number(materialData.sheet_width_in)
          : null,
      cost_per_sheet:
        materialData.cost_per_sheet !== null
          ? Number(materialData.cost_per_sheet)
          : null,
    };
    const response = await axios.post(`${API_BASE_URL}/materials`, dataToSend, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Create Material Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to create material");
  }
};

/** Updates an existing material */
const updateMaterial = async (materialId, materialData) => {
  try {
    const token = localStorage.getItem("accessToken");
    const dataToSend = {};
    if (Object.prototype.hasOwnProperty.call(materialData, "name")) {
      dataToSend.name = materialData.name;
    }
    if (Object.prototype.hasOwnProperty.call(materialData, "cost_per_sqft")) {
      dataToSend.cost_per_sqft = Number(materialData.cost_per_sqft);
    }
    if (Object.prototype.hasOwnProperty.call(materialData, "sheet_length_in")) {
      dataToSend.sheet_length_in =
        materialData.sheet_length_in !== null
          ? Number(materialData.sheet_length_in)
          : null;
    }
    if (Object.prototype.hasOwnProperty.call(materialData, "sheet_width_in")) {
      dataToSend.sheet_width_in =
        materialData.sheet_width_in !== null
          ? Number(materialData.sheet_width_in)
          : null;
    }
    if (Object.prototype.hasOwnProperty.call(materialData, "cost_per_sheet")) {
      dataToSend.cost_per_sheet =
        materialData.cost_per_sheet !== null
          ? Number(materialData.cost_per_sheet)
          : null;
    }
    const response = await axios.put(
      `${API_BASE_URL}/materials/${materialId}`,
      dataToSend,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Material Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update material");
  }
};

/** Deletes an existing material */
const deleteMaterial = async (materialId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.delete(
      `${API_BASE_URL}/materials/${materialId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Delete Material Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete material");
  }
};

/** Deletes multiple materials */
const deleteMaterialsBulk = async (materialIds) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.delete(`${API_BASE_URL}/materials/bulk`, {
      data: { material_ids: materialIds },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Bulk Delete Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete materials");
  }
};

/** ------------------- JOB API FUNCTIONS ------------------- */

/** Fetches all jobs with optional status filter */
const getJobs = async (statusFilter = null) => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      throw new Error("Missing authentication token. Please sign in again.");
    }
    // Ensure the URL has a trailing slash to match the backend route
    let url = `${API_BASE_URL}/jobs/`;
    if (statusFilter) {
      url += `?status=${statusFilter}`; // Append query params after the slash
    }

    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error("API Get Jobs Error:", error.response?.data || error.message);
    // Re-throw the specific error from the backend if available
    if (error.response?.data) {
      throw error.response.data;
    }
    // Otherwise, throw a generic error
    throw new Error("Failed to fetch jobs");
  }
};

/** Fetches a specific job by ID */
const getJob = async (jobId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.get(`${API_BASE_URL}/jobs/${jobId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error("API Get Job Error:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to fetch job");
  }
};

/** Creates a new job */
const createJob = async (jobData) => {
  try {
    // Get the token directly from localStorage
    const token = localStorage.getItem("accessToken");
    console.log(
      "Token found in localStorage:",
      token ? "Yes (length: " + token.length + ")" : "No",
    );

    if (!token) {
      console.error("No authentication token found in localStorage");
      throw new Error(
        "User not authenticated - no access token found in localStorage",
      );
    }

    // Use default axios (with interceptor) and relative URL
    const response = await axios.post(`${API_BASE_URL}/jobs/`, jobData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Log headers being sent for debugging
    console.log("Request headers:", {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.substring(0, 15)}...`,
    });

    // Make sure URL ends with trailing slash to avoid redirects
    return response.data;
  } catch (error) {
    console.error(
      "API Create Job Error:",
      error.response?.data || error.message,
    );
    if (error.response?.status === 401) {
      console.error("Authentication error details:", {
        headers: error.config?.headers,
        token: localStorage.getItem("accessToken") ? "Exists" : "Missing",
      });
    }
    throw error.response?.data || error;
  }
};

/** Updates an existing job (creates a new version) */
const updateJob = async (jobId, jobData) => {
  try {
    // Get the token directly from localStorage
    const token = localStorage.getItem("accessToken");
    console.log(
      "Token found in localStorage for updateJob:",
      token ? "Yes (length: " + token.length + ")" : "No",
    );

    if (!token) {
      console.error("No authentication token found in localStorage");
      throw new Error(
        "User not authenticated - no access token found in localStorage",
      );
    }

    // Use default axios (with interceptor) and relative URL
    const response = await axios.put(`${API_BASE_URL}/jobs/${jobId}`, jobData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    // Log headers being sent for debugging
    console.log("Request headers for update:", {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token.substring(0, 15)}...`, // Log only a prefix for security
    });

    // Remove trailing slash to match Flask route definition
    return response.data;
  } catch (error) {
    console.error(
      "API Update Job Error:",
      error.response?.data || error.message,
    );
    if (error.response?.status === 401) {
      console.error("Authentication error details (updateJob):", {
        headers: error.config?.headers, // Log headers from the failed request config
        token: localStorage.getItem("accessToken") ? "Exists" : "Missing",
      });
    }
    // Re-throw the specific error from the backend if available
    if (error.response?.data) {
      throw error.response.data;
    }
    // Otherwise, throw the original error or a generic one
    throw error;
  }
};

/** Fetches all versions of a job */
const getJobVersions = async (jobId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.get(`${API_BASE_URL}/jobs/${jobId}/versions`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Get Job Versions Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch job versions");
  }
};

/** Fetches a specific job version */
const getJobVersion = async (jobId, versionId) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log(
      `[apiService] getJobVersion(${jobId}, ${versionId}) - Token present: ${!!token}`,
    );

    const response = await axios.get(
      `${API_BASE_URL}/jobs/${jobId}/versions/${versionId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Get Job Version Error:",
      error.response?.data || error.message,
    );
    if (error.response?.status === 401) {
      console.error("Authentication error details (getJobVersion):", {
        headers: error.config?.headers,
        token: localStorage.getItem("accessToken") ? "Exists" : "Missing",
      });
    }
    throw error.response?.data || new Error("Failed to fetch job version");
  }
};

/** Deletes a job and all its versions */
const deleteJob = async (jobId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.delete(`${API_BASE_URL}/jobs/${jobId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Delete Job Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete job");
  }
};

/** ------------------- USER API FUNCTIONS ------------------- */

/** Updates a user's profile information, including preferences */
const apiUpdateUser = async (userData) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(
      `${API_BASE_URL}/auth/profile`,
      userData,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update User Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update user profile");
  }
};

/** Fetches application version info */
const getVersion = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/version`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Version Error:",
      error.response?.data || error.message,
    );
    return null; // Return null instead of throwing - version check shouldn't break app
  }
};

/** ------------------- MATERIAL IMPORT/EXPORT FUNCTIONS ------------------- */

/**
 * Preview column mapping for CSV import
 * @param {File} file - CSV file to preview
 * @param {boolean} useLlm - Whether to use LLM for column mapping
 * @returns {Promise<Object>} Preview result with headers, sample rows, and mapping
 */
const previewMaterialImport = async (file, useLlm = true) => {
  try {
    const token = localStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("use_llm", useLlm.toString());

    const response = await axios.post(
      `${API_BASE_URL}/materials/import/preview`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Preview Import Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to preview import");
  }
};

/**
 * Import materials from CSV file
 * @param {File} file - CSV file to import
 * @param {Object} options - Import options
 * @param {string} options.mode - 'update' (default), 'skip', or 'fail'
 * @param {boolean} options.dryRun - Preview changes without applying
/**
 * Import materials (either from file or pre-extracted materials array)
 * @param {File|null} file - PDF file to import (null if using pre-extracted materials)
 * @param {Object} options - Import options
 * @param {string} options.mode - Import mode: 'update', 'skip', 'fail'
 * @param {boolean} options.dryRun - If true, validate only
 * @param {Array} options.materials - Pre-extracted materials array (from smart extraction)
 * @returns {Promise<Object>} Import result with summary, warnings, errors
 */
const importMaterials = async (file, options = {}) => {
  try {
    const token = localStorage.getItem("accessToken");
    const { mode = "update", dryRun = false, materials = null } = options;

    // If we have pre-extracted materials, send as JSON
    if (materials && Array.isArray(materials)) {
      const response = await axios.post(
        `${API_BASE_URL}/materials/import`,
        {
          materials,
          mode,
          dry_run: dryRun,
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      return response.data;
    }

    // Legacy: send file with FormData (fallback)
    const formData = new FormData();
    formData.append("file", file);
    formData.append("mode", mode);
    formData.append("dry_run", dryRun.toString());

    const response = await axios.post(
      `${API_BASE_URL}/materials/import`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Import Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to import materials");
  }
};

/**
 * Suggest AI-driven rename updates for extracted materials.
 * @param {string} instruction - Rename guidance for the model
 * @param {Array} materials - Selected materials (subset of preview)
 * @returns {Promise<Object>} Suggestions with new names
 */
const renameImportedMaterials = async (instruction, materials) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await axios.post(
      `${API_BASE_URL}/materials/import/rename`,
      {
        instruction,
        materials,
      },
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Rename Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to rename materials");
  }
};

/**
 * Export all materials as CSV file
 * @returns {Promise<Blob>} CSV file as Blob
 */
const exportMaterials = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/materials/export`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Export Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to export materials");
  }
};

/**
 * Download CSV template for material import
 * @returns {Promise<Blob>} Template CSV file as Blob
 */
const getMaterialTemplate = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/materials/template`, {
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error(
      "API Get Template Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to get template");
  }
};

/** ------------------- USER MANAGEMENT FUNCTIONS (Admin Only) ------------------- */

/**
 * Get all users (admin only)
 * @returns {Promise<{users: Array, total: number}>}
 */
const getUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/users`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Users Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch users");
  }
};

/**
 * Get a specific user by ID (admin only)
 * @param {number} userId
 * @returns {Promise<Object>} User object
 */
const getUser = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error("API Get User Error:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to fetch user");
  }
};

/**
 * Update a user's role (admin only)
 * @param {number} userId
 * @param {string} role - 'admin' or 'estimator'
 * @returns {Promise<Object>} Updated user object
 */
const updateUserRole = async (userId, role) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/auth/users/${userId}/role`,
      { role },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update User Role Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update user role");
  }
};

/**
 * Delete a user (admin only)
 * @param {number} userId
 * @returns {Promise<Object>} Success message
 */
const deleteUser = async (userId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/auth/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error(
      "API Delete User Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete user");
  }
};

/**
 * Get global pricing settings (available to all users)
 * @returns {Promise<Object>} Pricing settings object
 */
const getPricingSettings = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/pricing-settings`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Pricing Settings Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch pricing settings");
  }
};

/**
 * Update global pricing settings (admin only)
 * @param {Object} settings - Pricing settings object
 * @returns {Promise<Object>} Updated settings
 */
const updatePricingSettings = async (settings) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/pricing-settings`,
      settings,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Pricing Settings Error:",
      error.response?.data || error.message,
    );
    throw (
      error.response?.data || new Error("Failed to update pricing settings")
    );
  }
};

/** ------------------- HISTORICAL SEARCH FUNCTIONS ------------------- */

/**
 * Search historical line items from pre-indexed costing sheets
 * @param {Object} params - Search parameters
 * @param {string} params.q - Search query (required)
 * @param {string} params.material - Material type filter (optional)
 * @param {string} params.pricing - Pricing model filter ('formula' or 'sqft', optional)
 * @param {number} params.limit - Max results (default: 50)
 * @param {number} params.offset - Pagination offset
 * @returns {Promise<Object>} Search results with pagination info
 */
const searchHistoricalItems = async ({
  q,
  material = null,
  pricing = null,
  limit = 50,
  offset = 0,
}) => {
  try {
    const params = new URLSearchParams({
      q,
      limit: String(limit),
      offset: String(offset),
    });
    if (material) params.append("material", material);
    if (pricing) params.append("pricing", pricing);

    const response = await axios.get(
      `${API_BASE_URL}/historical/search?${params.toString()}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Historical Search Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Historical search failed");
  }
};

/**
 * Get filter options for historical search (material types, job numbers)
 * @returns {Promise<Object>} Filter options
 */
const getHistoricalFilters = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/historical/filters`);
    return response.data;
  } catch (error) {
    console.error(
      "API Historical Filters Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch filters");
  }
};

/**
 * Get statistics about the historical index
 * @returns {Promise<Object>} Index statistics
 */
const getHistoricalStats = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/historical/stats`);
    return response.data;
  } catch (error) {
    console.error(
      "API Historical Stats Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch stats");
  }
};

/**
 * Update material_type for a single historical item
 * @param {number} itemId - Historical item ID
 * @param {string} materialType - New material type (or null to clear)
 * @returns {Promise<Object>} Updated item
 */
const updateHistoricalItemMaterial = async (itemId, materialType) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/historical/${itemId}/material`,
      { material_type: materialType },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Material Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update material");
  }
};

/**
 * Export items missing material_type as CSV
 * @returns {Promise<Blob>} CSV file blob
 */
const exportMissingMaterials = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/historical/export-missing-materials`,
      { responseType: "blob" },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Export Materials Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to export materials");
  }
};

/**
 * Bulk update material_type for multiple items
 * @param {Array<{id: number, material_type: string}>} updates - Array of updates
 * @returns {Promise<Object>} Result with updated_count and errors
 */
const bulkUpdateMaterials = async (updates) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/historical/bulk-update-materials`,
      { updates },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Bulk Update Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to bulk update materials");
  }
};

/**
 * Update a historical line item (name, description, material_type, pricing_model)
 * @param {number} itemId - Historical item ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated item
 */
const updateHistoricalItem = async (itemId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/historical/${itemId}`,
      updates,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Historical Item Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update historical item");
  }
};

/**
 * Delete a single historical line item
 * @param {number} itemId - Historical item ID
 * @returns {Promise<Object>} Result with deleted_id
 */
const deleteHistoricalItem = async (itemId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/historical/${itemId}`);
    return response.data;
  } catch (error) {
    console.error(
      "API Delete Historical Item Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete historical item");
  }
};

/**
 * Bulk delete multiple historical line items
 * @param {Array<number>} itemIds - Array of item IDs to delete
 * @returns {Promise<Object>} Result with deleted_count and errors
 */
const bulkDeleteHistoricalItems = async (itemIds) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/historical/bulk-delete`,
      { item_ids: itemIds },
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Bulk Delete Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to bulk delete items");
  }
};

/** ------------------- RATE CARD BUILDER FUNCTIONS ------------------- */

/**
 * Fetch all labor rate cards and finish rates.
 * @returns {Promise<{labor_cards: Array, finish_rates: Array}>}
 */
const getRateCards = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/rate-cards`);
    return response.data;
  } catch (error) {
    console.error(
      "API Get Rate Cards Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to fetch rate cards");
  }
};

/**
 * Create a new labor rate card.
 * @param {Object} cardData - Labor card fields (category required)
 * @returns {Promise<Object>} Created labor card
 */
const createLaborCard = async (cardData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rate-cards/labor`,
      cardData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Create Labor Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to create labor card");
  }
};

/**
 * Partially update an existing labor rate card.
 * @param {number} cardId
 * @param {Object} cardData - Fields to update
 * @returns {Promise<Object>} Updated labor card
 */
const updateLaborCard = async (cardId, cardData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/rate-cards/labor/${cardId}`,
      cardData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Labor Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update labor card");
  }
};

/**
 * Delete a labor rate card.
 * @param {number} cardId
 * @returns {Promise<Object>} Result message
 */
const deleteLaborCard = async (cardId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/rate-cards/labor/${cardId}`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Delete Labor Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to delete labor card");
  }
};

/**
 * Create a new finish rate.
 * @param {Object} finishData - {name, coats, cost_per_gallon, coverage_sqft_per_gal_per_coat, active}
 * @returns {Promise<Object>} Created finish rate (cost_per_sqft derived)
 */
const createFinishRate = async (finishData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rate-cards/finishes`,
      finishData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Create Finish Rate Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to create finish rate");
  }
};

/**
 * Update an existing finish rate.
 * @param {number} finishId
 * @param {Object} finishData - Fields to update
 * @returns {Promise<Object>} Updated finish rate
 */
const updateFinishRate = async (finishId, finishData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/rate-cards/finishes/${finishId}`,
      finishData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Update Finish Rate Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to update finish rate");
  }
};

/**
 * Live-preview a rate-card priced scenario.
 * @param {Object} payload - {length, width, height, geometry_method, labor_basis,
 *   labor_rates:{cnc,cut,fab,paint}, material_id|material_cost_per_sqft,
 *   finish_rate_id, product_based_multiplier, min_labor_hours}
 * @param {AbortSignal} [signal] - Optional abort signal to cancel stale requests
 * @returns {Promise<{engine: Object, pricing: Object}>}
 */
const previewRateCard = async (payload, signal = undefined) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rate-cards/preview`,
      payload,
      signal ? { signal } : undefined,
    );
    return response.data;
  } catch (error) {
    if (axios.isCancel?.(error) || error.name === "CanceledError") {
      throw error;
    }
    console.error(
      "API Preview Rate Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to preview rate card");
  }
};

/**
 * Derive per-department labor rates from a real measured item.
 * @param {Object} payload - {length, width, height, geometry_method, labor_basis,
 *   dept_hours:{cnc,cut,fab,paint}|null, total_hours|null, quantity}
 * @returns {Promise<{labor_units, sqft, rates|null, total_rate|null}>}
 */
const deriveRateCard = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rate-cards/derive`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Derive Rate Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to derive rates");
  }
};

/**
 * Attach a rate card to a product.
 * @param {Object} payload - {product_id, category, finish_rate_id, labor_basis,
 *   geometry_method, min_labor_hours}
 * @returns {Promise<{id, calculation_config}>}
 */
const attachRateCardToProduct = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/rate-cards/attach`,
      payload,
    );
    return response.data;
  } catch (error) {
    console.error(
      "API Attach Rate Card Error:",
      error.response?.data || error.message,
    );
    throw error.response?.data || new Error("Failed to attach rate card");
  }
};

/* ── AI line composer ─────────────────────────────────────────────────────
 * The composer proposes calculation INPUTS (stock, parts, department hours).
 * No response here carries a price, and nothing in this section computes one.
 * Errors are rethrown as a real Error carrying the backend's own sentence so
 * the dialog can show what the server said instead of "Request failed".
 */

/** Normalise an axios failure into an Error the composer UI can render. */
const composerError = (error, fallbackMessage) => {
  const data = error?.response?.data || {};
  const wrapped = new Error(data.error || error?.message || fallbackMessage);
  wrapped.status = error?.response?.status ?? null;
  wrapped.violations = Array.isArray(data.violations) ? data.violations : [];
  wrapped.details = data.details ?? null;
  wrapped.reason = data.reason ?? null;
  return wrapped;
};

/**
 * Availability probe. `{available:false}` is a normal answer, not an error —
 * the frontend hides the composer entry point entirely on it.
 */
const getComposerStatus = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/composer/status`);
    return response.data;
  } catch (error) {
    throw composerError(error, "Failed to read composer status");
  }
};

/**
 * Draft one line item. Synchronous and slow by design (5-20s), so callers pass
 * an AbortController signal and must be able to cancel.
 */
const draftLineItem = async (payload, { signal } = {}) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/composer/draft`,
      payload,
      { signal },
    );
    return response.data;
  } catch (error) {
    throw composerError(error, "Failed to draft the line item");
  }
};

/**
 * File the devised rate-card candidate that an accepted draft implies. The
 * card is created inactive — it never prices another job until an admin
 * activates it.
 */
const fileRateCardCandidate = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/composer/accept-candidate`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw composerError(error, "Failed to file the rate-card candidate");
  }
};

/**
 * File a typed-line "Quoted price" material override as a price observation
 * against the family's rate-card candidate, for Andy to review. Non-blocking
 * by design at every call site — see EstimateWorkbench's handleFileQuotedPrice
 * — a failure here never rolls back the edit that produced it.
 */
const filePriceObservation = async (payload) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/composer/price-observation`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw composerError(error, "Failed to file the price observation");
  }
};

/** ------------------- PRICING TRIAL ------------------- */

/**
 * The rafter-tail pricing trial: one row per trial quote plus the review
 * summary. `from`/`to` are YYYY-MM-DD on the job's created date; both
 * optional (omit for every trial quote).
 */
const getRafterTailTrial = async ({ from, to } = {}) => {
  try {
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    const response = await axios.get(`${API_BASE_URL}/trial/rafter-tail`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || new Error("Failed to load the pricing trial");
  }
};

/**
 * Record a quote's outcome and job actuals. A partial update that never
 * creates a new estimate version.
 */
const updateQuoteOutcome = async (jobId, fields) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/jobs/${jobId}/quote-outcome`,
      fields,
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || new Error("Failed to save the outcome");
  }
};

export {
  calculateCosts,
  getProducts,
  getProductTypes,
  getMaterials,
  createMaterial,
  updateMaterial,
  deleteMaterial,
  deleteMaterialsBulk,
  getJobs,
  getJob,
  createJob,
  updateJob,
  getJobVersions,
  getJobVersion,
  deleteJob,
  apiUpdateUser,
  getVersion,
  // Material import/export
  previewMaterialImport,
  importMaterials,
  renameImportedMaterials,
  exportMaterials,
  getMaterialTemplate,
  // Rate card builder
  getRateCards,
  createLaborCard,
  updateLaborCard,
  deleteLaborCard,
  createFinishRate,
  updateFinishRate,
  previewRateCard,
  deriveRateCard,
  attachRateCardToProduct,
  // Historical search
  searchHistoricalItems,
  getHistoricalFilters,
  getHistoricalStats,
  updateHistoricalItemMaterial,
  exportMissingMaterials,
  bulkUpdateMaterials,
  // Historical CRUD
  updateHistoricalItem,
  deleteHistoricalItem,
  bulkDeleteHistoricalItems,
  // User management (admin)
  getUsers,
  getUser,
  updateUserRole,
  deleteUser,
  // Pricing settings
  getPricingSettings,
  updatePricingSettings,
  // AI line composer
  getComposerStatus,
  draftLineItem,
  fileRateCardCandidate,
  filePriceObservation,
  // Pricing trial
  getRafterTailTrial,
  updateQuoteOutcome,
};
